import { useCallback, useEffect, useRef, useState } from "react";
import { requestJson } from "../../../../helpers/api";
import { NULL_OPTION_VALUE } from "./constants";
import { formatBranchLabel, normalizeBranchFilterValue, normalizeBranchKey } from "./formatters";
import type {
  ArticleCatalogEntry,
  ArticulosFiltrosResponse,
  ComprasApiResponse,
  ControlStockRow,
  InventarioApiResponse,
  MermasApiResponse,
  RecetaControlResponse,
  RemisionesApiResponse,
  VentasApiResponse,
} from "./types";

type DateRange = {
  mode: "day" | "week" | "month" | "range";
  startDate: string;
  endDate: string;
  previousDate: string;
  displayLabel: string;
  filterKey: string;
};

type UseControlStockDataResult = {
  serverOptions: string[];
  serverFilter: string;
  setServerFilter: (value: string) => void;
  excludeInternalBranches: boolean;
  setExcludeInternalBranches: (value: boolean) => void;
  branchOptions: string[];
  branchFilter: string[];
  setBranchFilter: (value: string[]) => void;
  familyFilter: string;
  setFamilyFilter: (value: string) => void;
  lineFilter: string;
  setLineFilter: (value: string) => void;
  familyOptions: Array<{ value: string; label: string }>;
  lineOptions: Array<{ value: string; label: string }>;
  familyLabelMap: Record<string, string>;
  lineLabelMap: Record<string, string>;
  recipeArticleSet: Set<string>;
  recipeCatalogStatus: "loading" | "ready" | "unavailable";
  records: ControlStockRow[];
  recordsLoading: boolean;
  recordsError: string | null;
  catalogMap: Record<string, ArticleCatalogEntry>;
};

const useControlStockData = (dateRange: DateRange): UseControlStockDataResult => {
  const [serverOptions, setServerOptions] = useState<string[]>([]);
  const [serverFilter, setServerFilter] = useState<string>("");
  const [excludeInternalBranches, setExcludeInternalBranches] = useState(false);
  const [branchFilter, setBranchFilter] = useState<string[]>([]);
  const [branchOptionsMap, setBranchOptionsMap] = useState<Record<string, string[]>>({});
  const [branchOptions, setBranchOptions] = useState<string[]>([]);
  const [familyFilter, setFamilyFilter] = useState<string>("todas");
  const [lineFilter, setLineFilter] = useState<string>("todas");
  const [familyOptions, setFamilyOptions] = useState<Array<{ value: string; label: string }>>([
    { value: "todas", label: "Todas las familias" },
  ]);
  const [lineOptions, setLineOptions] = useState<Array<{ value: string; label: string }>>([
    { value: "todas", label: "Todas las líneas" },
  ]);
  const [familyLabelMap, setFamilyLabelMap] = useState<Record<string, string>>({});
  const [lineLabelMap, setLineLabelMap] = useState<Record<string, string>>({});
  const [recipeArticleSet, setRecipeArticleSet] = useState<Set<string>>(() => new Set());
  const [recipeCatalogStatus, setRecipeCatalogStatus] = useState<"loading" | "ready" | "unavailable">("loading");
  const [records, setRecords] = useState<ControlStockRow[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState<string | null>(null);
  const [catalogMap, setCatalogMap] = useState<Record<string, ArticleCatalogEntry>>({});
  const hasInitializedServers = useRef(false);

  const applyCatalogToRows = useCallback(
    (
      rows: ControlStockRow[],
      nextCatalogMap: Record<string, ArticleCatalogEntry>,
    ): ControlStockRow[] => {
      if (rows.length === 0) {
        return rows;
      }
      let changed = false;
      const nextRows = rows.map((row) => {
        if (row.__isTotal) {
          return row;
        }
        const key = row.idArticulo?.trim().toUpperCase?.() ?? "";
        if (!key) {
          if (!row.__familiaId && !row.__lineaId) {
            return row;
          }
          changed = true;
          return {
            ...row,
            __familiaId: null,
            __lineaId: null,
            familia: row.familia ?? null,
            linea: row.linea ?? null,
          };
        }
        const entry = nextCatalogMap[key];
        if (!entry) {
          if (!row.__familiaId && !row.__lineaId && !row.familia && !row.linea) {
            return row;
          }
          changed = true;
          return {
            ...row,
            __familiaId: null,
            __lineaId: null,
            familia: row.familia ?? null,
            linea: row.linea ?? null,
          };
        }
        const familiaId = entry.familiaId;
        const lineaId = entry.lineaId;
        const familiaNombre = entry.familia ?? null;
        const lineaNombre = entry.linea ?? null;
        if (
          row.__familiaId === familiaId &&
          row.__lineaId === lineaId &&
          row.familia === familiaNombre &&
          row.linea === lineaNombre
        ) {
          return row;
        }
        changed = true;
        return {
          ...row,
          __familiaId: familiaId,
          __lineaId: lineaId,
          familia: familiaNombre,
          linea: lineaNombre,
        };
      });
      return changed ? nextRows : rows;
    },
    [],
  );

  useEffect(() => {
    let activo = true;
    const fetchOptions = async () => {
      try {
        const params = new URLSearchParams();
        if (excludeInternalBranches) {
          params.set("excluirSucursalesInternas", "1");
        }
        const endpoint = params.toString()
          ? `/api/informes/stock/compras/opciones?${params.toString()}`
          : "/api/informes/stock/compras/opciones";
        const respuesta = await requestJson<{
          servidores: string[];
          sucursales: Array<{
            server: string | null;
            sucursal: string;
            idDeposito: number | null;
          }>;
        }>(endpoint);

        if (!activo) return;

        const servidoresFormateados = (respuesta.servidores ?? []).map((item) =>
          item.trim().toUpperCase(),
        );
        setServerOptions(servidoresFormateados);
        if (!hasInitializedServers.current && servidoresFormateados.length > 0) {
          const defaultServer =
            servidoresFormateados.find((item) => item === "PARLA_SERVER") ??
            servidoresFormateados[0];
          setServerFilter((prev) => prev || defaultServer);
          hasInitializedServers.current = true;
        }

        const mapaSucursales = (respuesta.sucursales ?? []).reduce(
          (acc, item) => {
            const serverKey = item.server?.trim().toUpperCase();
            if (!serverKey) {
              return acc;
            }
            const nombre =
              item.sucursal && item.sucursal.trim().length > 0
                ? item.sucursal.trim()
                : "Sin depósito";
            const nombreNormalizado = normalizeBranchFilterValue(nombre);
            acc[serverKey] = acc[serverKey] ?? [];
            if (!acc[serverKey].includes(nombreNormalizado)) {
              acc[serverKey].push(nombreNormalizado);
            }
            return acc;
          },
          {} as Record<string, string[]>,
        );
        setBranchOptionsMap(mapaSucursales);
      } catch (error) {
        console.error("No se pudieron cargar los servidores:", error);
      }
    };

    void fetchOptions();
    return () => {
      activo = false;
    };
  }, [excludeInternalBranches]);

  useEffect(() => {
    if (!serverFilter) {
      setBranchOptions([]);
      return;
    }
    const opciones = branchOptionsMap[serverFilter] ?? [];
    setBranchOptions(opciones);
    if (branchFilter.length > 0) {
      const nextSelected = branchFilter.filter((item) => opciones.includes(item));
      if (nextSelected.length !== branchFilter.length) {
        setBranchFilter(nextSelected);
      }
    }
  }, [branchOptionsMap, serverFilter, branchFilter]);

  useEffect(() => {
    if (!serverFilter) {
      setRecipeArticleSet(new Set());
      setRecipeCatalogStatus("loading");
      return;
    }

    let activo = true;
    setRecipeCatalogStatus("loading");

    const fetchRecipeArticles = async () => {
      try {
        const params = new URLSearchParams({ server: serverFilter });
        const respuesta = await requestJson<RecetaControlResponse>(
          `/api/informes/stock/recetas/articulos?${params.toString()}`,
        );
        if (!activo) return;

        const ids = new Set(
          (respuesta.recetas ?? [])
            .map((item) => item.idarticulo?.trim())
            .filter((value): value is string => Boolean(value))
            .map((value) => value.toUpperCase()),
        );
        setRecipeArticleSet(ids);
        setRecipeCatalogStatus("ready");
      } catch (error) {
        if (!activo) return;
        console.error("No se pudieron cargar los artículos de recetas:", error);
        setRecipeArticleSet(new Set());
        setRecipeCatalogStatus("unavailable");
      }
    };

    void fetchRecipeArticles();
    return () => {
      activo = false;
    };
  }, [serverFilter]);

  useEffect(() => {
    if (!serverFilter) {
      setCatalogMap({});
      setFamilyLabelMap({});
      setLineLabelMap({});
      setFamilyOptions([{ value: "todas", label: "Todas las familias" }]);
      setLineOptions([{ value: "todas", label: "Todas las líneas" }]);
      setFamilyFilter("todas");
      setLineFilter("todas");
      setRecords((prev) => applyCatalogToRows(prev, {}));
      return;
    }

    let activo = true;

    const fetchCatalogFilters = async () => {
      try {
        const params = new URLSearchParams({ server: serverFilter });
        const respuesta = await requestJson<ArticulosFiltrosResponse>(
          `/api/informes/stock/articulos/filtros?${params.toString()}`,
        );
        if (!activo) return;

        const mapa: Record<string, ArticleCatalogEntry> = {};
        (respuesta.articulos ?? []).forEach((item) => {
          const key = item.idArticulo?.trim().toUpperCase?.();
          if (!key) return;
          const entry = {
            familiaId: item.idFamilia != null ? String(item.idFamilia) : null,
            familia: item.familia ?? null,
            lineaId: item.idLinea != null ? String(item.idLinea) : null,
            linea: item.linea ?? null,
          };
          mapa[key] = entry;
        });
        const familyLabels: Record<string, string> = {};
        (respuesta.familias ?? []).forEach((item) => {
          const key =
            item.id != null && Number.isFinite(item.id)
              ? String(item.id)
              : NULL_OPTION_VALUE;
          familyLabels[key] = item.nombre ?? "Sin familia";
        });
        const lineLabels: Record<string, string> = {};
        (respuesta.lineas ?? []).forEach((item) => {
          const key =
            item.id != null && Number.isFinite(item.id)
              ? String(item.id)
              : NULL_OPTION_VALUE;
          lineLabels[key] = item.nombre ?? "Sin línea";
        });
        Object.values(mapa).forEach((entry) => {
          const familyKey = entry.familiaId ?? NULL_OPTION_VALUE;
          const lineKey = entry.lineaId ?? NULL_OPTION_VALUE;
          if (!familyLabels[familyKey]) {
            familyLabels[familyKey] = entry.familia ?? "Sin familia";
          }
          if (!lineLabels[lineKey]) {
            lineLabels[lineKey] = entry.linea ?? "Sin línea";
          }
        });
        setCatalogMap(mapa);
        setFamilyLabelMap(familyLabels);
        setLineLabelMap(lineLabels);
        setFamilyFilter("todas");
        setLineFilter("todas");
        setRecords((prev) => applyCatalogToRows(prev, mapa));
      } catch (error) {
        if (!activo) return;
        console.error("No se pudieron cargar familias y líneas:", error);
        setCatalogMap({});
        setFamilyLabelMap({});
        setLineLabelMap({});
        setFamilyOptions([{ value: "todas", label: "Todas las familias" }]);
        setLineOptions([{ value: "todas", label: "Todas las líneas" }]);
        setFamilyFilter("todas");
        setLineFilter("todas");
        setRecords((prev) => applyCatalogToRows(prev, {}));
      }
    };

    void fetchCatalogFilters();
    return () => {
      activo = false;
    };
  }, [serverFilter, applyCatalogToRows]);

  useEffect(() => {
    if (!serverFilter) {
      return;
    }

    const entriesFromRows: ArticleCatalogEntry[] = [];
    const seenArticles = new Set<string>();
    records.forEach((row) => {
      if (row.__isTotal) return;
      const key = row.idArticulo?.trim().toUpperCase?.() ?? "";
      if (!key || seenArticles.has(key)) return;
      seenArticles.add(key);
      const catalogEntry = catalogMap[key];
      if (catalogEntry) {
        entriesFromRows.push(catalogEntry);
        return;
      }
      entriesFromRows.push({
        familiaId: row.__familiaId ?? null,
        familia: row.familia ?? null,
        lineaId: row.__lineaId ?? null,
        linea: row.linea ?? null,
      });
    });

    const lineMatchesCurrentFamily = (entry: ArticleCatalogEntry) =>
      familyFilter === "todas" ||
      (familyFilter === NULL_OPTION_VALUE
        ? entry.familiaId == null
        : entry.familiaId === familyFilter);
    const familyMatchesCurrentLine = (entry: ArticleCatalogEntry) =>
      lineFilter === "todas" ||
      (lineFilter === NULL_OPTION_VALUE
        ? entry.lineaId == null
        : entry.lineaId === lineFilter);

    const availableFamilyIds = new Set<string>();
    let hasNullFamily = false;
    entriesFromRows.forEach((entry) => {
      if (!familyMatchesCurrentLine(entry)) return;
      if (entry.familiaId == null) {
        hasNullFamily = true;
        return;
      }
      availableFamilyIds.add(entry.familiaId);
    });

    const availableLineIds = new Set<string>();
    let hasNullLine = false;
    entriesFromRows.forEach((entry) => {
      if (!lineMatchesCurrentFamily(entry)) return;
      if (entry.lineaId == null) {
        hasNullLine = true;
        return;
      }
      availableLineIds.add(entry.lineaId);
    });

    const nextFamilyOptions = [
      { value: "todas", label: "Todas las familias" },
      ...(hasNullFamily
        ? [
            {
              value: NULL_OPTION_VALUE,
              label: familyLabelMap[NULL_OPTION_VALUE] ?? "Sin familia",
            },
          ]
        : []),
      ...Array.from(availableFamilyIds)
        .sort((a, b) =>
          (familyLabelMap[a] ?? a).localeCompare(familyLabelMap[b] ?? b, "es"),
        )
        .map((id) => ({
          value: id,
          label: familyLabelMap[id] ?? id,
        })),
    ];

    const nextLineOptions = [
      { value: "todas", label: "Todas las líneas" },
      ...(hasNullLine
        ? [
            {
              value: NULL_OPTION_VALUE,
              label: lineLabelMap[NULL_OPTION_VALUE] ?? "Sin línea",
            },
          ]
        : []),
      ...Array.from(availableLineIds)
        .sort((a, b) =>
          (lineLabelMap[a] ?? a).localeCompare(lineLabelMap[b] ?? b, "es"),
        )
        .map((id) => ({
          value: id,
          label: lineLabelMap[id] ?? id,
        })),
    ];

    setFamilyOptions(nextFamilyOptions);
    setLineOptions(nextLineOptions);

    const familyValues = new Set(nextFamilyOptions.map((option) => option.value));
    if (!familyValues.has(familyFilter)) {
      setFamilyFilter("todas");
    }
    const lineValues = new Set(nextLineOptions.map((option) => option.value));
    if (!lineValues.has(lineFilter)) {
      setLineFilter("todas");
    }
  }, [
    serverFilter,
    records,
    familyFilter,
    lineFilter,
    familyLabelMap,
    lineLabelMap,
    catalogMap,
  ]);

  useEffect(() => {
    if (!serverFilter) {
      setRecords([]);
      return;
    }

    let activo = true;

    const appendBranchFilters = (search: URLSearchParams) => {
      if (branchFilter.length === 0) {
        return;
      }
      const uniqueBranches = Array.from(
        new Set(
          branchFilter
            .map((item) => normalizeBranchFilterValue(item))
            .filter((item) => item.length > 0),
        ),
      );
      uniqueBranches.forEach((branch) => {
        search.append("sucursal", branch);
      });
    };

    const buildRangeParams = (cacheKey?: string) => {
      const search = new URLSearchParams({
        server: serverFilter,
        fechaDesde: dateRange.startDate,
        fechaHasta: dateRange.endDate,
      });
      if (excludeInternalBranches) {
        search.set("excluirSucursalesInternas", "1");
      }
      if (dateRange.mode === "day" && dateRange.endDate) {
        search.set("fecha", dateRange.endDate);
      }
      appendBranchFilters(search);
      if (cacheKey) {
        search.set("_ts", cacheKey);
      }
      return search;
    };

    const buildInventarioParams = (fecha: string, cacheKey?: string) => {
      const search = new URLSearchParams({
        server: serverFilter,
        fecha,
      });
      if (excludeInternalBranches) {
        search.set("excluirSucursalesInternas", "1");
      }
      appendBranchFilters(search);
      if (cacheKey) {
        search.set("_ts", cacheKey);
      }
      return search;
    };

    const cacheKey = Date.now().toString();
    const params = buildRangeParams(cacheKey);
    const remisionesParams = new URLSearchParams(params);
    const mermasParams = new URLSearchParams(params);
    const ventasParams = new URLSearchParams(params);

    const inventarioInicialParams = buildInventarioParams(dateRange.previousDate, cacheKey);
    const inventarioFinalParams = buildInventarioParams(dateRange.endDate, cacheKey);

    const fetchData = async () => {
      setRecordsLoading(true);
      setRecordsError(null);
      try {
        const [compras, inventariosIniciales, inventariosFinales, remisiones, ventas, mermas] = await Promise.all([
          requestJson<ComprasApiResponse>(
            `/api/informes/stock/compras/control?${params.toString()}`,
          ),
          requestJson<InventarioApiResponse>(
            `/api/informes/stock/inventarios/control?${inventarioInicialParams.toString()}`,
          ),
          requestJson<InventarioApiResponse>(
            `/api/informes/stock/inventarios/final?${inventarioFinalParams.toString()}`,
          ),
          requestJson<RemisionesApiResponse>(
            `/api/informes/stock/remisiones/control?${remisionesParams.toString()}`,
          ),
          requestJson<VentasApiResponse>(
            `/api/informes/stock/ventas/control?${ventasParams.toString()}`,
          ),
          requestJson<MermasApiResponse>(
            `/api/informes/stock/mermas/control?${mermasParams.toString()}`,
          ),
        ]);

        if (!activo) return;

        const buildKey = (server: string, sucursal: string, articulo: string) =>
          `${server}::${sucursal}::${articulo}`;

        const rowsMap = new Map<string, ControlStockRow>();
        const rangeLabel = dateRange.displayLabel;
        const rangeKey = dateRange.filterKey;
        const hasSingleBranch = branchFilter.length === 1;
        const singleBranch = hasSingleBranch ? branchFilter[0] : "";
        const resolveSucursal = (value?: string | null) => {
          if (value && String(value).trim().length > 0) {
            return value;
          }
          if (hasSingleBranch) {
            return singleBranch;
          }
          return null;
        };

        const ensureRow = (config: {
          server?: string | null;
          sucursal?: string | null;
          idArticulo?: string | null;
          articulo?: string | null;
          unidadMedida?: string | null;
          valorUnitario?: number | null;
        }) => {
          const serverValue =
            config.server?.trim().toUpperCase() || serverFilter || "SIN_SERVER";
          const resolvedSucursal = resolveSucursal(config.sucursal);
          const hasSucursalInRow =
            resolvedSucursal != null && String(resolvedSucursal).trim().length > 0;
          const sucursalLabel = hasSingleBranch
            ? formatBranchLabel(singleBranch)
            : hasSucursalInRow
              ? formatBranchLabel(resolvedSucursal)
              : "Todas las sucursales";
          const sucursalKey = hasSingleBranch
            ? normalizeBranchKey(singleBranch)
            : hasSucursalInRow
              ? normalizeBranchKey(resolvedSucursal)
              : "GLOBAL";
          const articuloValue =
            config.idArticulo?.trim() ||
            config.articulo?.trim() ||
            "SIN-CODIGO";
          const key = buildKey(serverValue, sucursalKey, articuloValue);
          let row = rowsMap.get(key);
          if (!row) {
            row = {
              idArticulo: articuloValue,
              articulo: config.articulo ?? config.idArticulo ?? "Sin descripción",
              server: serverValue,
              sucursal: sucursalLabel,
              fechaReferencia: rangeLabel,
              valorUnitario: Number(config.valorUnitario ?? 0),
              unidadMedida: config.unidadMedida ?? "",
              familia: null,
              linea: null,
              inventarioInicial: 0,
              compras: 0,
              remisionPositiva: 0,
              remisionNegativa: 0,
              ventas: 0,
              ajustePositivo: 0,
              ajusteNegativo: 0,
              teorico: 0,
              inventarioFinal: 0,
              diferencia: 0,
              __details: {},
              __hasInventarioInicial: false,
              __hasInventarioFinal: false,
              __rangeKey: rangeKey,
              __familiaId: null,
              __lineaId: null,
            };
            rowsMap.set(key, row);
          } else {
            if (config.articulo && !row.articulo) {
              row.articulo = config.articulo;
            }
            if ((config.valorUnitario ?? 0) > 0) {
              row.valorUnitario = Number(config.valorUnitario);
            }
            row.sucursal = sucursalLabel;
            if (config.unidadMedida) {
              row.unidadMedida = config.unidadMedida;
            }
            row.fechaReferencia = rangeLabel;
            row.__rangeKey = rangeKey;
          }
          return row;
        };

        const inventarioFecha = dateRange.previousDate;
        inventariosIniciales.forEach((item) => {
          const cantidad = Number(item.cantidad ?? 0);
          const promedio =
            item.costoUnitarioPromedio ??
            (cantidad > 0 ? (item.monto ?? 0) / cantidad : null) ??
            0;
          const row = ensureRow({
            server: item.server,
            sucursal: item.sucursal,
            idArticulo: item.idArticulo,
            articulo: item.articulo,
            unidadMedida: item.unidadMedida,
            valorUnitario: promedio,
          });
          row.inventarioInicial += cantidad;
          row.__details = row.__details ?? {};
          row.__hasInventarioInicial = true;
          const inventarioDetails = row.__details.inventarioInicial ?? [];
          inventarioDetails.push({
            fecha: inventarioFecha,
            precioUnitario: Number(promedio ?? 0),
            cantidad,
            idMovimiento:
              item.idMovimiento != null ? Number(item.idMovimiento) : null,
            documento: null,
            deposito: row.sucursal,
          });
          row.__details.inventarioInicial = inventarioDetails;
        });

        inventariosFinales.forEach((item) => {
          const cantidad = Number(item.cantidad ?? 0);
          const promedio =
            item.costoUnitarioPromedio ??
            (cantidad > 0 ? (item.monto ?? 0) / cantidad : null) ??
            0;
          const row = ensureRow({
            server: item.server,
            sucursal: item.sucursal,
            idArticulo: item.idArticulo,
            articulo: item.articulo,
            unidadMedida: item.unidadMedida,
            valorUnitario: promedio,
          });
          row.inventarioFinal += cantidad;
          row.__hasInventarioFinal = true;
          row.__details = row.__details ?? {};
          const finalDetails = row.__details.inventarioFinal ?? [];
          finalDetails.push({
            fecha: dateRange.endDate,
            precioUnitario: Number(promedio ?? 0),
            cantidad,
            idMovimiento:
              item.idMovimiento != null ? Number(item.idMovimiento) : null,
            documento: null,
            deposito: row.sucursal,
          });
          row.__details.inventarioFinal = finalDetails;
        });

        compras.forEach((item) => {
          const cantidad = Number(item.cantidad ?? 0);
          const monto = Number(item.monto ?? 0);
          const valorUnitario =
            item.costoUnitarioPromedio ??
            (cantidad > 0 ? monto / cantidad : item.costoUnitarioPromedio ?? 0);
          const row = ensureRow({
            server: item.server,
            sucursal: item.deposito,
            idArticulo: item.idArticulo,
            articulo: item.articulo,
            valorUnitario: valorUnitario ?? undefined,
          });

          row.compras += cantidad;
          row.__details = row.__details ?? {};
          row.__details.compras =
            item.movimientos?.map((mov) => ({
              fecha: mov.fecha ?? null,
              precioUnitario: Number(mov.precioUnitario ?? valorUnitario ?? 0),
              cantidad: Number(mov.cantidad ?? 0),
              idMovimiento:
                mov.idMovimiento != null ? Number(mov.idMovimiento) : null,
              documento:
                mov.documento && String(mov.documento).length > 0
                  ? String(mov.documento)
                  : null,
              deposito: formatBranchLabel(mov.deposito ?? item.deposito),
            })) ?? [];
        });

        remisiones.forEach((item) => {
          const remisionSucursalGlobal =
            item.sucursal ??
            item.detallesPositivos?.[0]?.deposito ??
            item.detallesNegativos?.[0]?.deposito ??
            null;
          const row = ensureRow({
            server: item.server ?? undefined,
            sucursal: resolveSucursal(remisionSucursalGlobal),
            idArticulo: item.idArticulo,
            articulo: item.articulo,
            unidadMedida: item.unidadMedida ?? undefined,
            valorUnitario: item.costoUnitarioPromedio ?? undefined,
          });
          const remPos = Number(item.remisionPositiva ?? 0);
          const remNeg = Number(item.remisionNegativa ?? 0);
          row.remisionPositiva += remPos;
          row.remisionNegativa += remNeg;
          row.__details = row.__details ?? {};

          if (item.detallesPositivos?.length) {
            const actuales = row.__details.remisionPositiva ?? [];
            row.__details.remisionPositiva = [
              ...actuales,
              ...item.detallesPositivos.map((mov) => ({
                fecha: mov.fecha ?? null,
                precioUnitario: Number(mov.precioUnitario ?? row.valorUnitario ?? 0),
                cantidad: Number(mov.cantidad ?? 0),
                idMovimiento:
                  mov.idMovimiento != null ? Number(mov.idMovimiento) : null,
                documento: mov.documento ?? null,
                deposito: formatBranchLabel(mov.deposito),
              })),
            ];
          }

          if (item.detallesNegativos?.length) {
            const actuales = row.__details.remisionNegativa ?? [];
            row.__details.remisionNegativa = [
              ...actuales,
              ...item.detallesNegativos.map((mov) => ({
                fecha: mov.fecha ?? null,
                precioUnitario: Number(mov.precioUnitario ?? row.valorUnitario ?? 0),
                cantidad: Number(mov.cantidad ?? 0),
                idMovimiento:
                  mov.idMovimiento != null ? Number(mov.idMovimiento) : null,
                documento: mov.documento ?? null,
                deposito: formatBranchLabel(mov.deposito),
              })),
            ];
          }
        });

        ventas.forEach((item) => {
          const row = ensureRow({
            server: item.server ?? undefined,
            sucursal: resolveSucursal(item.sucursal ?? null),
            idArticulo: item.idArticulo,
            articulo: item.articulo,
            unidadMedida: item.unidadMedida ?? undefined,
            valorUnitario: item.costoUnitarioPromedio ?? undefined,
          });
          const ventasCantidad = Number(item.ventas ?? 0);
          row.ventas += ventasCantidad;
          row.__details = row.__details ?? {};

          if (item.detalles?.length) {
            const actuales = row.__details.ventas ?? [];
            row.__details.ventas = [
              ...actuales,
              ...item.detalles.map((mov) => ({
                fecha: mov.fecha ?? null,
                precioUnitario: Number(mov.precioUnitario ?? row.valorUnitario ?? 0),
                cantidad: Number(mov.cantidad ?? 0),
                idMovimiento:
                  mov.idMovimiento != null ? Number(mov.idMovimiento) : null,
                documento: mov.documento && mov.documento.length > 0 ? mov.documento : null,
                deposito: formatBranchLabel(mov.sucursal),
                idArticuloVenta:
                  mov.idArticuloVenta && mov.idArticuloVenta.length > 0
                    ? mov.idArticuloVenta
                    : null,
                articuloVenta:
                  mov.articuloVenta && mov.articuloVenta.length > 0
                    ? mov.articuloVenta
                    : null,
                idReceta:
                  mov.idReceta && mov.idReceta.length > 0
                    ? mov.idReceta
                    : null,
                receta:
                  mov.receta && mov.receta.length > 0
                    ? mov.receta
                    : null,
                cantidadVendida:
                  mov.cantidadVendida != null ? Number(mov.cantidadVendida) : null,
              })),
            ];
          }
        });

        mermas.forEach((item) => {
          const mermaSucursalGlobal = item.sucursal ?? item.detalles?.[0]?.sucursal ?? null;
          const row = ensureRow({
            server: item.server ?? undefined,
            sucursal: resolveSucursal(mermaSucursalGlobal),
            idArticulo: item.idArticulo,
            articulo: item.articulo,
            unidadMedida: item.unidadMedida ?? undefined,
            valorUnitario: item.costoUnitarioPromedio ?? undefined,
          });
          const mermaCantidad = Number(item.merma ?? 0);
          row.ajusteNegativo += mermaCantidad;
          row.__details = row.__details ?? {};

          if (item.detalles?.length) {
            const actuales = row.__details.ajusteNegativo ?? [];
            row.__details.ajusteNegativo = [
              ...actuales,
              ...item.detalles.map((mov) => ({
                fecha: mov.fecha ?? null,
                precioUnitario: Number(mov.precioUnitario ?? row.valorUnitario ?? 0),
                cantidad: Number(mov.cantidad ?? 0),
                idMovimiento:
                  mov.idMovimiento != null ? Number(mov.idMovimiento) : null,
                documento: mov.documento ?? null,
                deposito: formatBranchLabel(mov.sucursal),
              })),
            ];
          }
        });

        const mapped = Array.from(rowsMap.values()).map((row) => {
          row.teorico =
            row.inventarioInicial +
            row.compras +
            row.remisionPositiva -
            row.remisionNegativa -
            row.ventas +
            row.ajustePositivo -
            row.ajusteNegativo;
          row.diferencia = row.inventarioFinal - row.teorico;
          return row;
        });

        setRecords(applyCatalogToRows(mapped, catalogMap));
      } catch (error) {
        if (!activo) return;
        console.error(error);
        setRecordsError(
          error instanceof Error
            ? error.message
            : "No pudimos cargar las compras.",
        );
        setRecords([]);
      } finally {
        if (activo) {
          setRecordsLoading(false);
        }
      }
    };

    void fetchData();
    return () => {
      activo = false;
    };
  }, [
    serverFilter,
    branchFilter,
    excludeInternalBranches,
    dateRange,
    applyCatalogToRows,
    catalogMap,
  ]);

  return {
    serverOptions,
    serverFilter,
    setServerFilter,
    excludeInternalBranches,
    setExcludeInternalBranches,
    branchOptions,
    branchFilter,
    setBranchFilter,
    familyFilter,
    setFamilyFilter,
    lineFilter,
    setLineFilter,
    familyOptions,
    lineOptions,
    familyLabelMap,
    lineLabelMap,
    recipeArticleSet,
    recipeCatalogStatus,
    records,
    recordsLoading,
    recordsError,
    catalogMap,
  };
};

export type { DateRange, UseControlStockDataResult };
export default useControlStockData;
